import { api } from "./client";
import type {
  CampaignSuggestion,
  Pipeline,
  PipelineBoard,
  PipelineMembership,
} from "../types";

export interface ListPipelinesParams {
  q?: string;
  status?: string;
  limit?: number;
  offset?: number;
  [key: string]: unknown;
}

export interface MemberWriteResult {
  contact_id: string;
  membership?: PipelineMembership | null;
  target_membership?: PipelineMembership | null;
  already_active?: boolean;
  reopened?: boolean;
  unparked?: boolean;
  stats?: Record<string, number> | null;
}

// Board writes answer with the board – with the parked cards when they are shown.
const parkedQuery = (includeParked: boolean) => (includeParked ? "?include_parked=true" : "");

export const pipelinesApi = {
  list: (params: ListPipelinesParams = {}) =>
    api.get<Pipeline[]>("/api/v1/pipelines", params),
  get: (id: string) => api.get<Pipeline>(`/api/v1/pipelines/${id}`),
  getBySlug: (slug: string) =>
    api.get<Pipeline>(`/api/v1/pipelines/slug/${slug}`),
  board: (id: string, includeParked = false) =>
    api.get<PipelineBoard>(
      `/api/v1/pipelines/${id}/board`,
      includeParked ? { include_parked: true } : undefined,
    ),
  moveBoardCard: (
    id: string,
    body: { contact_id: string; target_stage_id: string },
    includeParked = false,
  ) =>
    api.post<PipelineBoard>(`/api/v1/pipelines/${id}/board/move${parkedQuery(includeParked)}`, body),
  reorderBoardColumn: (
    id: string,
    body: { stage_id: string; contact_ids: string[] },
    includeParked = false,
  ) =>
    api.post<PipelineBoard>(`/api/v1/pipelines/${id}/board/reorder${parkedQuery(includeParked)}`, body),
  enroll: (slug: string, contactId: string, body: { stage?: string; notes?: string } = {}) =>
    api.put<MemberWriteResult>(
      `/api/v1/pipelines/slug/${slug}/members/${contactId}`,
      body,
    ),
  moveMember: (
    slug: string,
    contactId: string,
    body: {
      to_stage?: string;
      to_pipeline?: string;
      close_status?: "goal_reached" | "dropped";
      park?: boolean;
      unpark?: boolean;
      until?: string;
      notes?: string;
    },
  ) =>
    api.post<MemberWriteResult>(
      `/api/v1/pipelines/slug/${slug}/members/${contactId}/move`,
      body,
    ),
  campaignSuggestions: (slug: string) =>
    api.get<CampaignSuggestion[]>(
      `/api/v1/pipelines/slug/${slug}/campaign-suggestions`,
    ),
  setCategory: (id: string, category: "lead" | "opportunity" | null) =>
    api.patch<Pipeline>(`/api/v1/pipelines/${id}`, { category }),
  reorder: (ids: string[]) =>
    api.post<{ ok: boolean }>("/api/v1/pipelines/reorder", { ids }),
};
